import { el, clear } from '../lib/dom.js';

/**
 * Tabela com busca (com botao limpar), ordenacao por cabecalho e paginacao.
 * Cada coluna aceita, alem de { chave, rotulo, render, ordenavel }:
 *   - dica:    texto do icone (i) de ajuda no cabecalho, explicando o que e a coluna.
 *   - largura: largura fixa da coluna (ex.: '90px', '9%'); ativa o layout proporcional
 *              (table-layout: fixed). Colunas SEM largura dividem o espaco restante por igual.
 *   - alinhar: 'centro' | 'direita' (padrao a esquerda) — alinha cabecalho e celulas.
 *   - truncar: true -> a celula corta com '…' e mostra o texto inteiro no hover (cursor de ajuda).
 */
export function renderTabela(container, { colunas, linhas, buscarEm = [], acoes = null, porPagina = 10 }) {
  const estado = { termo: '', ordCol: null, ordDir: 1, pagina: 1 };
  const temLargura = colunas.some((c) => c.largura);
  const alinhaCss = (a) => (a === 'centro' ? 'center' : a === 'direita' ? 'right' : null);

  const busca = el('input', { type: 'text', class: 'tabela-busca__input', placeholder: 'Buscar...',
    onInput: (e) => { estado.termo = e.target.value.toLowerCase().trim(); estado.pagina = 1; limpar.style.display = e.target.value ? '' : 'none'; pintar(); } });
  const limpar = el('button', { type: 'button', class: 'input-x', title: 'Limpar', style: 'display:none',
    onClick: () => { busca.value = ''; estado.termo = ''; estado.pagina = 1; limpar.style.display = 'none'; pintar(); busca.focus(); } },
    [el('i', { class: 'ph ph-x' })]);

  const corpo = el('tbody');
  const rotulos = {}; // chave -> span do rotulo (atualiza a seta sem perder o icone de dica)
  const cabecalho = el('tr', {}, colunas.map((c) => {
    const lab = el('span', { class: 'th-rotulo', text: c.rotulo });
    rotulos[c.chave] = lab;
    const filhos = [lab];
    if (c.dica) filhos.push(el('i', { class: 'ph ph-info col-info', title: c.dica, onClick: (e) => e.stopPropagation() }));
    const th = el('th', { onClick: c.ordenavel === false ? null : () => ordenarPor(c.chave), dataset: { col: c.chave } }, filhos);
    const al = alinhaCss(c.alinhar); if (al) th.style.textAlign = al;
    return th;
  }));
  const rodape = el('div', { class: 'paginacao' });

  const colgroup = el('colgroup', {}, colunas.map((c) => el('col', c.largura ? { style: `width:${c.largura}` } : {})));
  const tabela = el('table', { class: 'tabela' + (temLargura ? ' tabela--fixa' : '') }, [colgroup, el('thead', {}, [cabecalho]), corpo]);

  const wrap = el('div', { class: 'tabela-wrap' }, [
    el('div', { class: 'tabela-topo' }, [el('div', { class: 'tabela-busca' }, [busca, limpar]), acoes || el('span')]),
    tabela,
    rodape,
  ]);
  clear(container).append(wrap);

  function ordenarPor(chave) {
    if (estado.ordCol === chave) estado.ordDir *= -1;
    else { estado.ordCol = chave; estado.ordDir = 1; }
    pintar();
  }

  function filtrarOrdenar() {
    let r = linhas;
    if (estado.termo && buscarEm.length) {
      const termoDig = estado.termo.replace(/\D/g, '');
      r = r.filter((l) => buscarEm.some((k) => {
        const v = String(l[k] ?? '').toLowerCase();
        return v.includes(estado.termo) || (termoDig && v.replace(/\D/g, '').includes(termoDig));
      }));
    }
    if (estado.ordCol) {
      r = [...r].sort((a, b) => {
        const va = a[estado.ordCol], vb = b[estado.ordCol];
        return (va < vb ? -1 : va > vb ? 1 : 0) * estado.ordDir;
      });
    }
    return r;
  }

  function pintar() {
    const dados = filtrarOrdenar();
    const totalPag = Math.max(1, Math.ceil(dados.length / porPagina));
    estado.pagina = Math.min(estado.pagina, totalPag);
    const ini = (estado.pagina - 1) * porPagina;
    const pagina = dados.slice(ini, ini + porPagina);

    clear(corpo);
    if (!pagina.length) {
      corpo.append(el('tr', {}, [el('td', { class: 'tabela__vazio', colspan: colunas.length }, 'Nenhum registro.')]));
    } else {
      for (const linha of pagina) {
        corpo.append(el('tr', {}, colunas.map((c) => {
          const td = el('td', {}, c.render ? c.render(linha) : (linha[c.chave] ?? ''));
          const al = alinhaCss(c.alinhar); if (al) td.style.textAlign = al;
          if (c.truncar) { td.classList.add('td--trunc'); const t = td.textContent || ''; if (t) td.title = t; }
          return td;
        })));
      }
    }

    colunas.forEach((c) => {
      const lab = rotulos[c.chave];
      if (!lab) return;
      lab.textContent = (c.chave === estado.ordCol) ? `${c.rotulo} ${estado.ordDir === 1 ? '▲' : '▼'}` : c.rotulo;
    });

    clear(rodape).append(
      el('span', { text: `${dados.length} registro(s) · página ${estado.pagina}/${totalPag}` }),
      el('button', { class: 'btn', disabled: estado.pagina <= 1, onClick: () => { estado.pagina--; pintar(); } }, '‹'),
      el('button', { class: 'btn', disabled: estado.pagina >= totalPag, onClick: () => { estado.pagina++; pintar(); } }, '›'),
    );
  }

  pintar();
  return { atualizar: (novas) => { linhas = novas; pintar(); } };
}
